
import React, { useState } from 'react';
import { Project, Client, Language } from '../types';
import Button from './ui/Button';
import Card from './ui/Card';
import SupportTicketModal from './SupportTicketModal';

interface ClientAreaProps {
  client: Client;
  projects: Project[];
  language: Language;
  onLogout: () => void;
  onProjectClick: (project: Project) => void;
  t: any;
}

const ClientArea = ({ client, projects, language, onLogout, onProjectClick, t }: ClientAreaProps) => {
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const ct = t.clientArea;

  return (
    <div className="container mx-auto px-4 py-12 animate-fade-in">
      <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-white mb-2">
            {ct.welcome} <span className="text-blue-400">{client.name}</span>
          </h1>
          <p className="text-slate-400 text-lg">
            {ct.subtitle}
          </p>
        </div>
        <Button onClick={onLogout} variant="ghost" className="text-slate-400 hover:text-white border-slate-700">
          {ct.logout}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {projects.length > 0 ? (
          projects.map((project) => (
            <Card key={project.id} className="group hover:border-blue-500/50 transition-all cursor-pointer overflow-hidden flex flex-col h-full" onClick={() => onProjectClick(project)}>
              <div className="p-6 flex-grow">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-slate-700/50 rounded-xl group-hover:scale-110 transition-transform">
                    {project.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">{project.name}</h3>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{typeof project.city === 'string' ? project.city : (project.city?.[language] || project.city?.['es'] || '')}</p>
                  </div>
                </div>
                
                <p className="text-slate-400 text-sm line-clamp-3 mb-6">
                  {typeof project.publicDescription === 'string' ? project.publicDescription : (project.publicDescription?.[language] || project.publicDescription?.['es'] || '')}
                </p>

                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-800">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-green-400 uppercase tracking-widest">{t.projectsModal?.progress || 'Progreso'}</span>
                    <span className="text-xs text-slate-500">{project.privateDetails.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-1.5 mb-3">
                    <div 
                      className="bg-green-500 h-full rounded-full transition-all duration-1000 shadow-[0_0_10px_rgba(34,197,94,0.3)]" 
                      style={{ width: `${project.privateDetails.progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-white font-medium">
                    {typeof project.privateDetails?.status === 'string' ? project.privateDetails.status : (project.privateDetails?.status?.[language] || project.privateDetails?.status?.['es'] || '')}
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-slate-700/20 border-t border-slate-800 flex justify-between items-center">
                <span className="text-xs text-blue-400 font-bold group-hover:translate-x-1 transition-transform inline-flex items-center">
                  {ct.confidentialDetails} 
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
                {project.partnershipAvailable && (
                  <span className="text-[10px] bg-green-500/10 text-green-500 px-2 py-0.5 rounded border border-green-500/20">
                    {ct.investmentOpportunity}
                  </span>
                )}
              </div>
            </Card>
          ))
        ) : (
          <div className="col-span-full py-20 text-center bg-slate-800/50 rounded-3xl border border-dashed border-slate-700">
            <p className="text-slate-500">{ct.noProjects}</p>
          </div>
        )}
      </div>

      <div className="mt-16 bg-gradient-to-br from-blue-600/10 to-indigo-600/10 rounded-3xl p-8 border border-blue-500/20">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-bold text-white mb-4">{ct.supportTitle}</h2>
          <p className="text-slate-400 mb-6">
            {ct.supportText}
          </p>
          <div className="flex gap-4">
             <Button variant="primary" className="px-6" onClick={() => setIsSupportModalOpen(true)}>
               {ct.supportButton}
             </Button>
             <Button variant="ghost" className="px-6 text-slate-300">
               {ct.guideButton}
             </Button>
          </div>
        </div>
      </div>

      <SupportTicketModal 
        isOpen={isSupportModalOpen} 
        onClose={() => setIsSupportModalOpen(false)}
        client={client}
      />
    </div>
  );
};

export default ClientArea;
